import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import toast from 'react-hot-toast';
import { showToast } from '../utils/toast';
import { Helmet } from 'react-helmet-async';
import './LoginPage.css';
import './SocialLinks.css';
import logo from '../assets/pokeshani-logo.png';

const SignupPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    password: ''
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      fullName: '',
      email: '',
      password: ''
    };

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const loadingToast = showToast.loading('Creating your creator account...');
      try {
        const siteUrl = window.location.origin;
        const referralCode = searchParams.get('ref');
        
        // Perform the signup
        // First attempt to sign up
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
              is_creator: true
            },
            emailRedirectTo: `${siteUrl}/dashboard`
          }
        });

        // If signup was successful and we have a referral code
        if (!authError && authData.user && referralCode) {
          try {
            // Set referral code in session for trigger to use (with fallback)
            try {
              await supabase.rpc('set_referral_code', { code: referralCode });
            } catch (rpcError) {
              console.log('RPC function not available, using direct approach');
            }
            
            // Also handle it directly in case trigger fails or RPC is unavailable
            // Try multiple approaches to get marketer data due to RLS policies
            let marketerData = null;
            let marketerError = null;
            
            // Method 1: Try the new RPC function first (bypasses RLS)
            try {
              const { data: rpcData, error: rpcError } = await supabase
                .rpc('validate_referral_code', { ref_code: referralCode });
              
              if (rpcData && rpcData.length > 0) {
                marketerData = {
                  id: rpcData[0].marketer_id,
                  referral_code: rpcData[0].referral_code,
                  is_active: rpcData[0].is_active
                };
              } else {
                marketerError = rpcError || { message: 'No marketer found via RPC' };
              }
            } catch (rpcError) {
              console.log('RPC function not available, trying direct query');
              
              // Method 2: Fallback to direct query (original approach)
              const { data: directData, error: directError } = await supabase
                .from('marketers')
                .select('id, referral_code, is_active')
                .eq('referral_code', referralCode)
                .single();
              
              marketerData = directData;
              marketerError = directError;
            }

            if (marketerData && !marketerError) {
              // Check if marketer is active
              const isActive = marketerData.is_active === true;
              console.log('Found marketer:', marketerData.id, 'is_active:', marketerData.is_active, 'isActive:', isActive);
              
              if (isActive) {
                // Record the signup
                console.log('Attempting to insert signup record for marketer:', marketerData.id);
                const { error: signupError } = await supabase
                  .from('marketer_signups')
                  .insert([
                    { 
                      marketer_id: marketerData.id,
                      signup_timestamp: new Date().toISOString()
                    }
                  ]);

                console.log('Signup insert result:', { signupError });

                if (!signupError) {
                  // Try to use the new RPC function first, fallback to direct update
                  try {
                    await supabase.rpc('increment_marketer_signups', {
                      marketer_id: marketerData.id
                    });
                    console.log('Successfully recorded referral signup using RPC');
                  } catch (rpcError) {
                    // Fallback to direct update
                    const { data: currentStats } = await supabase
                      .from('marketers')
                      .select('total_signups')
                      .eq('id', marketerData.id)
                      .single();

                    await supabase
                      .from('marketers')
                      .update({ 
                        total_signups: (currentStats?.total_signups || 0) + 1,
                        last_signup_at: new Date().toISOString()
                      })
                      .eq('id', marketerData.id);
                    
                    console.log('Successfully recorded referral signup using fallback method');
                  }
                } else {
                  console.error('Error inserting signup record:', signupError);
                }
              } else {
                console.log('Marketer not active:', marketerData);
              }
            } else {
              console.log('No marketer found for referral code:', referralCode);
              if (marketerError) {
                console.error('Marketer query error:', marketerError);
              }
            }
          } catch (err) {
            console.error('Error processing referral signup:', err);
            // Don't let referral tracking errors affect the main signup flow
          }
        }

        if (authError) {
          toast.dismiss(loadingToast);
          if (authError.message.includes('email')) {
            showToast.error('This email is already registered. Please use a different email or try logging in.');
          } else {
            showToast.error(authError.message || 'Failed to create account. Please try again.');
          }
          return;
        }

        if (authData.user) {
          toast.dismiss(loadingToast);
          showToast.success('Account created successfully! Please check your email to verify your account.');
          navigate('/');
        }
      } catch (error: any) {
        toast.dismiss(loadingToast);
        showToast.error(error.message || 'Failed to create account. Please try again.');
      }
    }
  };

  const handleGoogleSignup = async () => {
    const loadingToast = showToast.loading('Signing up with Google...');
    try {
      const siteUrl = window.location.origin;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${siteUrl}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        toast.dismiss(loadingToast);
        showToast.error(error.message);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      showToast.error('An unexpected error occurred during Google signup');
      console.error('Error during Google signup:', error);
    }
  };

  return (
    <div className="landing-container signup-page">
      <div className="social-links">
        <a
          href="https://x.com/PokeShani"
          target="_blank"
          rel="noopener noreferrer"
          className="social-link"
          aria-label="Twitter"
        >
          <svg viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>
        <a
          href="https://www.instagram.com/pokeshaniofficial?igsh=NHF3aHd4aXY5Y3Ux"
          target="_blank"
          rel="noopener noreferrer"
          className="social-link instagram"
          aria-label="Instagram"
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.072 4.849-.072zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>
      </div>
      <Helmet>
        <title>Sign Up for PokeShani - Create Your Digital Product Store | 0% Platform Fees</title>
        <meta name="description" content="Join PokeShani and start selling your digital products with zero platform fees. Create courses, ebooks, and more with instant PayPal payouts. Sign up now!" />
        <meta name="keywords" content="sign up, create account, digital products, online courses, ebooks, zero platform fees, instant payouts, PayPal payments" />
        <meta property="og:title" content="Sign Up for PokeShani - Start Your Digital Product Business" />
        <meta property="og:description" content="Create your PokeShani account today. Sell digital products with 0% platform fees and instant PayPal payouts." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Join PokeShani - Your Digital Product Platform" />
        <meta name="twitter:description" content="Start your digital product business with no platform fees. Sign up now for instant PayPal payouts." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Sign Up for PokeShani",
            "description": "Create your account to start selling digital products with no platform fees and instant PayPal payouts",
            "url": "https://pokeshani.com/signup"
          })}
        </script>
      </Helmet>
      <div className="help-link">
        <Link to="/faq" className="help-button">Help</Link>
      </div>
      <div className="auth-section">
        <div className="auth-content">
          <div className="welcome-section">
            <img src={logo} alt="PokeShani Logo" className="logo signup-logo" />
            <h1>Create your account</h1>
            <p>Already have an account? <Link to="/login" className="signup-link">Login</Link></p>
          </div>
          
          {/* Terms Notice */}
          <div className="terms-notice">
            <p>Please read our <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/faq">FAQ</Link> before creating an account</p>
          </div>
          
          {/* Google Signup Button - Moved to top */}
          <button 
            type="button" 
            className="google-login-btn"
            onClick={handleGoogleSignup}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Sign up with Google</span>
          </button>
          
          {/* Divider */}
          <div className="divider">
            <span>or</span>
          </div>
          
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                name="fullName"
                placeholder="Enter full name"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group password-input">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
            <button type="submit" className="login-btn">Sign up</button>
          </form>
        </div>
      </div>
      
      <div className="showcase-section">
        <div className="showcase-content">
          <h2>Welcome to PokeShani</h2>
          <p>Your Ultimate Platform for Digital Product Sales</p>
          <div className="feature-grid">
            <div className="feature-item">
              <h3>Easy to Start</h3>
              <p>Create your digital product listings in minutes with our intuitive interface</p>
            </div>
            <div className="feature-item">
              <h3>Secure Payments</h3>
              <p>Integrated PayPal payments ensure safe and reliable transactions</p>
            </div>
            <div className="feature-item">
              <h3>Real-time Analytics</h3>
              <p>Track your sales, revenue, and customer insights with detailed dashboards</p>
            </div>
            <div className="feature-item">
              <h3>Keep 100% Earnings</h3>
              <p>No platform fees - keep all your earnings with instant payouts</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;